import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';

const CookieBannerContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: 'rgba(11, 8, 19, 0.98)',
  border: '1px solid #00F0FF',
  borderRadius: '8px',
  padding: '20px 30px',
  boxShadow: '0 0 30px rgba(0, 240, 255, 0.3)',
  zIndex: 10000,
  maxWidth: '90%',
  width: '600px',
  [theme.breakpoints.down('md')]: {
    width: '90%',
    padding: '15px 20px',
    bottom: '10px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '95%',
    padding: '12px 15px',
    bottom: '5px',
  },
}));

const CookieTitle = styled(Typography)(({ theme }) => ({
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '12px',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginBottom: '10px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    marginBottom: '8px',
  },
}));

const CookieText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '13px',
  lineHeight: '1.6',
  marginBottom: '15px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    marginBottom: '12px',
  },
}));

const CookieLink = styled('span')(({ theme }) => ({
  color: '#00F0FF',
  textDecoration: 'underline',
  cursor: 'pointer',
  '&:hover': {
    color: '#39FF14',
  },
}));

const CookieButton = styled(Button)(({ theme }) => ({
  border: '2px solid #00F0FF',
  color: '#00F0FF',
  padding: '8px 20px',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  marginRight: '10px',
  '&:hover': {
    borderColor: '#00F0FF',
    color: '#00F0FF',
    backgroundColor: 'rgba(0, 240, 255, 0.1)',
    boxShadow: '0 0 15px rgba(0, 240, 255, 0.5)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    backgroundColor: '#00F0FF',
    color: '#0B0813',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '6px 15px',
    fontSize: '9px',
    marginRight: '8px',
  },
}));

const CookieButtonSecondary = styled(Button)(({ theme }) => ({
  border: '2px solid #6B6A78',
  color: '#6B6A78',
  padding: '8px 20px',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#6B6A78',
    color: '#FFFFFF',
    backgroundColor: 'rgba(107, 106, 120, 0.1)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '6px 15px',
    fontSize: '9px',
  },
}));

const CookieBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const hasAcceptedCookies = localStorage.getItem('cookiesAccepted');
    if (!hasAcceptedCookies) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('cookieConsent', 'all');
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('cookieConsent', 'essential');
    setIsVisible(false);
  };

  const handlePrivacyPolicy = () => {
    window.open('/privacy', '_blank');
  };

  if (!isVisible) {
    return null;
  }

  return (
    <CookieBannerContainer>
      <CookieTitle>🍪 COOKIE CONSENT</CookieTitle>
      <CookieText>
        We use cookies to enhance your experience and analyze site traffic. 
        By clicking "Accept All", you consent to our use of cookies. 
        See our <CookieLink onClick={handlePrivacyPolicy}>Privacy Policy</CookieLink> for details.
      </CookieText>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <CookieButton onClick={handleAccept}>
          ACCEPT ALL
        </CookieButton>
        <CookieButtonSecondary onClick={handleDecline}>
          ESSENTIAL ONLY
        </CookieButtonSecondary>
      </Box>
    </CookieBannerContainer>
  );
};

export default CookieBanner;
